"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { trackROICalculatorInteraction, trackQuoteRequest } from "@/lib/analytics";

const bedroomPresets = [
  { label: "Studio", rate: 120, occupancy: 68 },
  { label: "1 BR", rate: 165, occupancy: 72 },
  { label: "2 BR", rate: 245, occupancy: 75 },
  { label: "3 BR", rate: 340, occupancy: 73 },
  { label: "4 BR", rate: 480, occupancy: 70 },
  { label: "5+ BR", rate: 650, occupancy: 65 },
];

export default function ROICalculator() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const viewTracked = useRef(false);
  const [bedroomIndex, setBedroomIndex] = useState(2);
  const [nightlyRate, setNightlyRate] = useState(245);
  const [occupancy, setOccupancy] = useState(75);
  const [managementFee] = useState(18);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            if (!viewTracked.current) {
              viewTracked.current = true;
              trackROICalculatorInteraction('view');
            }
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll(".reveal-hidden");
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const handleBedroomChange = (index: number) => {
    setBedroomIndex(index);
    setNightlyRate(bedroomPresets[index].rate);
    setOccupancy(bedroomPresets[index].occupancy);
    trackROICalculatorInteraction('adjust', {
      bedroom_type: bedroomPresets[index].label,
    });
  };

  const daysPerMonth = 30;
  const bookedNights = Math.round((occupancy / 100) * daysPerMonth);
  const grossMonthly = bookedNights * nightlyRate;
  const managementCost = grossMonthly * (managementFee / 100);
  const cleaningCost = bookedNights * 125;
  const netMonthly = grossMonthly - managementCost - cleaningCost;
  const annualRevenue = netMonthly * 12;

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(val);

  return (
    <section id="roi-calculator" ref={sectionRef} className="section-padding bg-navy">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-12 reveal-hidden">
          <span className="text-gold text-xs font-bold uppercase tracking-widest block mb-3">
            Revenue Estimator
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl text-white mb-4">
            How Much Could Your{" "}
            <span className="italic font-light text-gold">Property Earn?</span>
          </h2>
          <p className="text-white/60 text-lg max-w-xl mx-auto">
            Based on real PCB market data. Adjust the sliders to see your estimated monthly income.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start reveal-hidden">
          {/* Controls */}
          <div className="bg-navy-light/40 border border-white/10 rounded-3xl p-8">
            {/* Bedroom Selector */}
            <div className="mb-8">
              <label className="text-white/70 text-sm font-medium block mb-4">
                Property Size
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {bedroomPresets?.map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => handleBedroomChange(i)}
                    className={`py-2.5 px-2 rounded-xl text-xs font-semibold transition-all duration-300 ${
                      bedroomIndex === i
                        ? "bg-gold text-navy shadow-lg"
                        : "bg-white/8 text-white/60 hover:bg-white/15 hover:text-white"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Nightly Rate Slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-white/70 text-sm font-medium">Avg. Nightly Rate</label>
                <span className="text-gold font-bold text-lg">{formatCurrency(nightlyRate)}</span>
              </div>
              <input
                type="range"
                min={75}
                max={1200}
                step={5}
                value={nightlyRate}
                onChange={(e) => setNightlyRate(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #C9A84C ${((nightlyRate - 75) / (1200 - 75)) * 100}%, rgba(255,255,255,0.15) 0%)`,
                }}
              />
              <div className="flex justify-between text-white/30 text-xs mt-1.5">
                <span>$75</span>
                <span>$1,200</span>
              </div>
            </div>

            {/* Occupancy Slider */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <label className="text-white/70 text-sm font-medium">Occupancy Rate</label>
                <span className="text-gold font-bold text-lg">{occupancy}%</span>
              </div>
              <input
                type="range"
                min={30}
                max={95}
                step={1}
                value={occupancy}
                onChange={(e) => setOccupancy(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #C9A84C ${((occupancy - 30) / (95 - 30)) * 100}%, rgba(255,255,255,0.15) 0%)`,
                }}
              />
              <div className="flex justify-between text-white/30 text-xs mt-1.5">
                <span>30%</span>
                <span>95%</span>
              </div>
            </div>

            {/* PCB Market Benchmark */}
            <div className="bg-gold/10 border border-gold/20 rounded-2xl p-4">
              <p className="text-gold text-xs font-semibold uppercase tracking-wider mb-2">PCB Market Benchmark</p>
              <p className="text-white/60 text-xs leading-relaxed">
                Average PCB occupancy: <span className="text-white font-medium">72%</span> · Peak season (Jun–Aug): <span className="text-white font-medium">91%</span> · Avg. nightly rate: <span className="text-white font-medium">$285</span>
              </p>
            </div>
          </div>

          {/* Results */}
          <div className="flex flex-col gap-5">
            {/* Main Revenue Card */}
            <div className="bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 rounded-3xl p-8 text-center">
              <p className="text-gold/70 text-sm font-medium uppercase tracking-wider mb-2">Estimated Monthly Net Revenue</p>
              <p className="font-heading text-6xl text-gold font-bold mb-1">
                {formatCurrency(netMonthly)}
              </p>
              <p className="text-white/40 text-sm">after management & cleaning fees</p>
            </div>

            {/* Breakdown */}
            <div className="bg-navy-light/40 border border-white/10 rounded-3xl p-6 space-y-4">
              <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">Monthly Breakdown</h4>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Booked nights ({bookedNights}/30)</span>
                <span className="text-white font-medium">{bookedNights} nights</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Gross rental income</span>
                <span className="text-emerald-400 font-semibold">{formatCurrency(grossMonthly)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Management fee ({managementFee}%)</span>
                <span className="text-red-400 font-medium">−{formatCurrency(managementCost)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Cleaning services</span>
                <span className="text-red-400 font-medium">−{formatCurrency(cleaningCost)}</span>
              </div>
              <div className="border-t border-white/10 pt-4 flex items-center justify-between">
                <span className="text-white font-semibold">Net Monthly</span>
                <span className="text-gold font-bold text-lg">{formatCurrency(netMonthly)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">Projected Annual</span>
                <span className="text-gold font-bold text-xl">{formatCurrency(annualRevenue)}</span>
              </div>
            </div>

            <Link
              href="/contact"
              onClick={() => {
                trackROICalculatorInteraction('cta_click', {
                  estimated_monthly: netMonthly,
                  estimated_annual: annualRevenue,
                  bedroom_type: bedroomPresets[bedroomIndex].label,
                });
                trackQuoteRequest('full_service', 'roi_calculator_cta');
              }}
              className="btn-gold py-4 rounded-2xl font-semibold text-base text-center flex items-center justify-center gap-2.5 group"
            >
              Get My Personalized Estimate
              <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <p className="text-white/30 text-xs text-center">
              Estimates based on PCB market averages. Actual results may vary.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
